import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const LABELS = {
  currentRole: "Current role",
  yearsExperience: "Years experience",
  targetRole: "Target role",
  weeklyStudyHours: "Weekly study hours",
  timelineWeeks: "Timeline (weeks)",
  learningStyle: "Learning style",
  primaryCloud: "Primary cloud",
  motivation: "Motivation",
};

export default function Profile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [sub, setSub] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([api.get("/profile"), api.get("/subscriptions/me")])
      .then(([p, s]) => {
        setProfile(p.data);
        setSub(s.data);
      })
      .catch((e) => setError(errMsg(e, "Unable to load profile")));
  }, []);

  if (error) return <p data-testid="profile-error" className="text-sm text-destructive">{error}</p>;
  if (!profile) return <div className="grid place-items-center py-24" data-testid="profile-loading"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-10 fade-up max-w-3xl" data-testid="profile-page">
      <div>
        <p className="overline text-primary">profile</p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-display font-black tracking-tighter">{user?.name}</h1>
        <p className="mt-2 font-mono text-sm text-muted-foreground">{user?.email} · {user?.role}</p>
      </div>

      <div className="border border-border divide-y divide-border" data-testid="profile-details">
        {Object.keys(LABELS).map((k) =>
          profile[k] !== undefined && profile[k] !== "" ? (
            <div key={k} className="flex justify-between gap-6 px-5 py-3.5 text-sm">
              <span className="text-muted-foreground">{LABELS[k]}</span>
              <span className="font-mono text-right">{String(profile[k]).replace(/_/g, " ")}</span>
            </div>
          ) : null
        )}
        {!Object.keys(profile).length && <p className="p-5 text-sm text-muted-foreground">No onboarding profile yet.</p>}
      </div>

      <div className="border border-border p-6" data-testid="profile-subscription">
        <p className="overline text-muted-foreground">subscription</p>
        <p className="mt-3 font-display text-xl font-bold tracking-tight">
          {sub?.access?.isTrial ? "Free trial · full access" : sub?.plan?.name || "Free"}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">{sub?.plan?.tagline}</p>
        {sub?.access?.isTrial && (
          <p className="mt-2 font-mono text-xs text-primary" data-testid="profile-trial-left">
            {sub.access.trialDaysLeft} {sub.access.trialDaysLeft === 1 ? "day" : "days"} left · ends {new Date(sub.access.trialEndsAt).toLocaleDateString()}
          </p>
        )}
        {sub?.access?.trialExpired && (
          <p className="mt-2 font-mono text-xs text-destructive" data-testid="profile-trial-expired">
            Trial ended — upgrade to restore full access.
          </p>
        )}
        <p className="mt-3 font-mono text-xs text-muted-foreground">status: {sub?.access?.status} · provider: {sub?.access?.provider}</p>
        <Link to="/pricing" className="inline-block mt-5" data-testid="profile-upgrade">
          <Button variant="outline" className="rounded-none">Manage plan</Button>
        </Link>
      </div>

      <Link to="/onboarding" data-testid="profile-edit-onboarding">
        <Button className="rounded-none">Update onboarding answers</Button>
      </Link>
    </div>
  );
}
